
	import { Component, Vue, Watch } from 'vue-property-decorator';
	import PatientTable from '@/components/patient-list/PatientTable.vue';
	import { Observer } from 'mobx-vue';
	import { Patient } from '@/store/engine/openapi/generated';
	import { OrderWithPatient } from '@/store/DoctorStore';
	import OrderTable from '@/components/patient-list/OrderTable.vue';
	import Loading from '@/components/Loading.vue';
	import GlobalSearch, { SearchResult } from '@/components/modals/GlobalSearch.vue';
	import UtilsStore from '@/store/utilsStore';
	import DualButtonModal from '@/components/modals/DualButtonModal.vue';

	@Observer
	@Component({
		components: { GlobalSearch, Loading, PatientTable, OrderTable, DualButtonModal },
		filters: {
			fixedLength(val: string) {
				return val.length >= 43 ? val.slice(0, 40) + '...' : val;
			}
		}
	})
	export default class PatientRecall extends Vue {
		private tabs = ['All', 'Record Created', 'Scanned', 'Prescription', 'Orders'];
		private activeTab = this.tabs[0];
		private privacy = false;
		private orders?: OrderWithPatient[] = [];
		private patients?: Patient[] = [];
		private currentPatients: { patients?: Patient[] } = { patients: undefined };
		private ordersWithPatient: { orders?: OrderWithPatient[] } = { orders: undefined };
		private filterByDoctor: string = '';
		private patientChosen = false;
		private corruptedPatient = false;
		private legacyPatient = false;

		@Watch('filterByDoctor')
		private onDoctorChanged() {
			this.filterPatients(this.activeTab);
			this.filterOrders();
		}

		async created() {
			await this.fetchAllOrders();
			await this.filterPatients(this.activeTab);
		}

		private async fetchAllOrders() {
			const orderFetch = await this.$state.doctor.fetchPatientOrders();
			this.orders = orderFetch.orders;
			await this.filterOrders();
			// we check on .count for case of no orders to stop fetch
			// FIXME(HPCLINIC-382): Reenable once HPCLINIC-382 is fixed
			// if (this.$state.doctor.ordersPaginationPercentage < 100 && this.$state.doctor.ordersPagination.count > 0) {
			// 	await this.fetchAllOrders();
			// }
		}

		async activated() {
			this.patientChosen = false;
			this.activeTab = this.tabs[0];
			await this.filterPatients(this.activeTab);
			await this.filterOrders();
		}

		private async filterOrders() {
			this.ordersWithPatient.orders = await this.$state.doctor.getPatientsOrders();
			if (this.filterByDoctor) {
				this.ordersWithPatient.orders = this.ordersWithPatient.orders?.filter((p) => p.patient.user?.operators?.currentOwner === this.filterByDoctor);
			}
		}

		get isCurrentTabEmpty() {
			const currentArray = this.activeTab === 'Orders' ? this.ordersWithPatient.orders : this.currentPatients.patients;
			return currentArray?.length === 0;
		}

		get isCurrentTabFetched() {
			const currentArray = this.activeTab === 'Orders' ? this.ordersWithPatient.orders : this.currentPatients.patients;
			return currentArray !== undefined;
		}

		private async filterPatients(option: string) {
			this.patients = await this.$state.doctor.getPatients();
			const patients = this.patients;
			if (!patients) {
				return;
			}
			switch (option) {
				case 'All':
					this.currentPatients.patients = patients;
					break;

				case 'Record Created':
					this.currentPatients.patients = patients.filter((p) => UtilsStore.getHighestPriorityStatus(p) === 'SESSION_STATUS_SESSION_CREATED');
					break;

				case 'Scanned':
					this.currentPatients.patients = patients.filter(
						(p) => UtilsStore.getHighestPriorityStatus(p) === 'SESSION_STATUS_MEASUREMENT_DATA_CAPTURED'
					);
					break;

				case 'Prescription':
					this.currentPatients.patients = patients.filter(
						(p) =>
							UtilsStore.getHighestPriorityStatus(p) === 'SESSION_STATUS_PRESCRIPTION_CREATED' ||
							UtilsStore.getHighestPriorityStatus(p) === 'SESSION_STATUS_PRESCRIPTION_COMPLETED' ||
							UtilsStore.getHighestPriorityStatus(p) === 'SESSION_STATUS_PRESCRIPTION_UPDATED'
					);
					break;

				default:
					this.currentPatients.patients = patients;
					break;
			}

			if (this.filterByDoctor) {
				this.currentPatients.patients = this.currentPatients.patients.filter((p) => p.user?.operators?.currentOwner === this.filterByDoctor);
			}
		}

		private isActive(option: string) {
			return this.activeTab === option ? 'selectionButton selected' : 'selectionButton';
		}

		private async setActiveTab(option: string) {
			this.activeTab = option;
			await this.filterPatients(option);
		}

		private back() {
			this.$router.push('patients');
		}

		private next() {
			this.$state._patient.clear();
			this.$router.push('getting-started');
		}

		private patientDelete(patient: Patient) {
			this.$state.doctor.deletePatient(patient);
			this.filterPatients(this.activeTab);
		}

		public globalSearchSkip(search: SearchResult) {
			if (search.order) {
				const patient: OrderWithPatient = { order: search.order, patient: search.patient };
				this.orderFlowSkip(patient);
			} else {
				this.patientFlowSkip(search.patient);
			}
		}

		public isMigratedProfile(patient: Patient) {
			return patient.medicalSolutionData?.birthDateTimestamp === -2210025600000;
		}

		public async orderFlowSkip(patient: OrderWithPatient) {
			if (this.patientChosen) {
				console.warn('Order was already chosen');
				return;
			}
			this.patientChosen = true;
			try {
				this.$state.leroy.restart(true);
				if (patient.patient.user) {
					await this.$state._patient.fetchPatientData(patient.patient.user?.userID);
					await this.$state._patient.createNewScanningSession(patient.patient.user?.userID); // for reorder
				}
				await this.$state.order.loadFromOrder(patient.order.orderID);
			} catch (e) {
				if (this.isMigratedProfile(patient.patient)) {
					this.legacyPatient = true;
				} else {
					this.corruptedPatient = true;
				}
				console.error(e);
				await this.$state._patient.clear();
				this.patientChosen = false;
				return;
			}
			let status = UtilsStore.getHighestPriorityStatus(patient.patient);

			const fp = await this.$engine.profile.getFitstationProfile(this.$state._patient.userRecordID);
			if (fp && fp.data) {
				const scans = await this.$engine.biometric.getScans(fp.data.lastValidScanningSession);
			}
			if (!patient.patient.user) {
				this.corruptedPatient = true;

				throw new Error('No user found');
			}

			//TODO: Is it good for all cases?
			switch (status) {
				case 'SESSION_STATUS_PRESCRIPTION_CREATED':
					await this.$router.push('/summary?id=' + patient.patient.user.userID);
					break;
				case 'SESSION_STATUS_PRESCRIPTION_COMPLETED':
					await this.$router.push('/summary?id=' + patient.patient.user.userID);
					break;
				case 'SESSION_STATUS_PRESCRIPTION_UPDATED':
					await this.$router.push('/summary?id=' + patient.patient.user.userID);
					break;
				case 'SESSION_STATUS_ORDER_CREATED':
					await this.$router.push('/summary?id=' + patient.patient.user.userID);
					break;
				case 'SESSION_STATUS_REPAIR_ORDER_CREATED':
					await this.$router.push('/summary?id=' + patient.patient.user.userID);
					break;
				default:
					await this.$router.push('/summary?id=' + patient.patient.user.userID);
					break;
			}
			return;
		}

		public async patientFlowSkip(patient: Patient) {
			if (this.patientChosen) return;
			this.patientChosen = true;
			try {
				this.$state.leroy.restart(true);
				if (patient.user) {
					await this.$state._patient.openPatient(patient.user?.userID);
				}
			} catch (e) {
				if (this.isMigratedProfile(patient)) {
					this.legacyPatient = true;
				} else {
					this.corruptedPatient = true;
				}
				console.error(e);
				await this.$state._patient.clear();
				this.patientChosen = false;
				return;
			}
			let status = UtilsStore.getHighestPriorityStatus(patient);
			const fp = await this.$engine.profile.getFitstationProfile(this.$state._patient.userRecordID);
			//@ts-ignore
			const scans = await this.$engine.biometric.getScans(fp.data.lastValidScanningSession);

			if (!patient.user) {
				this.corruptedPatient = true;

				throw new Error('No user found');
			}

			switch (status) {
				case 'SESSION_STATUS_SESSION_CREATED':
					await this.$router.push('/scan-settings?id=' + patient.user.userID);
					break;
				case 'SESSION_STATUS_REPAIR_ORDER_CREATED':
					await this.$router.push('/summary?id=' + patient.user.userID);
					break;
				case 'SESSION_STATUS_ORDER_DELIVERED':
					await this.$router.push('/summary?id=' + patient.user.userID);
					break;
				case 'SESSION_STATUS_ORDER_CANCELLED':
					await this.$router.push('/summary?id=' + patient.user.userID);
					break;
				case 'SESSION_STATUS_MEASUREMENT_DATA_CAPTURED':
					if (scans && scans.filter((el) => el.isComplete).length == 0) {
						await this.$router.push('/order-styles?id=' + patient.user.userID);
					} else {
						await this.$router.push('/results?id=' + patient.user.userID);
					}
					break;
				case 'SESSION_STATUS_PRESCRIPTION_CREATED':
					await this.$router.push('/summary?id=' + patient.user.userID);
					break;
				case 'SESSION_STATUS_PRESCRIPTION_COMPLETED':
					await this.$router.push('/summary?id=' + patient.user.userID);
					break;
				case 'SESSION_STATUS_PRESCRIPTION_UPDATED':
					await this.$router.push('/summary?id=' + patient.user.userID);
					break;
				case 'SESSION_STATUS_ORDER_CREATED':
					await this.$router.push('/summary?id=' + patient.user.userID);
					break;
				default:
					await this.$router.push('/order-styles?id=' + patient.user.userID);
					break;
			}
			return;
		}
	}
